import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(ScrollTrigger);
let mm = gsap.matchMedia();

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

export class HomeHeroAnimation {
  private element: HTMLElement;
  private title: HTMLHeadElement | null;
  private subhead: HTMLHeadElement | null;
  private buttonSet: HTMLElement | null;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.title = this.element.querySelector(
        ".home-hero__text-content .home-hero__title",
      );
      this.subhead = this.element.querySelector(".home-hero__subhead");
      this.buttonSet = this.element.querySelector(".button-link-set");
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateHeadline();
        this.animateSbuhead();
        this.animateButtons();
      });
    }
  }

  private animateHeadline() {
    document.fonts.ready.then(() => {
      gsap.set(this.title, { opacity: 1 });
      let split;

      SplitText.create(this.title, {
        type: "lines",
        mask: "lines",
        onSplit: (self) => {
          split = gsap.from(self.lines, {
            y: -20,
            autoAlpha: 0,
            stagger: 0.25,
            duration: 0.75,
            delay: 1,
            ease: "expo.out",
            onComplete: () => {
              self.revert();
            },
          });
          return split;
        },
      });
    });
  }

  private animateSbuhead() {
    gsap.set(this.subhead, {
      autoAlpha: 0,
    });

    gsap.to(this.subhead, {
      autoAlpha: 1,
      delay: 1.5,
      duration: 1,
      ease: "power2.out",
    });
  }

  private animateButtons() {
    gsap.set(this.buttonSet, {
      autoAlpha: 0,
      clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)",
    });

    gsap.to(this.buttonSet, {
      autoAlpha: 1,
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
      delay: 1.5,
      duration: 0.75,
      ease: "power2.out",
    });
  }
}

export class HomeFeaturetteAnimation {
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;

      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateFeaturette();
      });
    }
  }

  private animateFeaturette() {
    gsap.set(this.element, {
      autoAlpha: 0,
      y: 100,
    });

    gsap.to(this.element, {
      autoAlpha: 1,
      y: 0,
      delay: 1.5,
      duration: 1,
      ease: "power2.out",
    });
  }
}

export class HomeImageAnimation {
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateImage();
      });
    }
  }

  private animateImage() {
    gsap.set(this.element, {
      clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)",
    });

    gsap.to(this.element, {
      scrollTrigger: {
        trigger: this.element,
        markers: false,
        start: "top bottom-=150px",
        end: "bottom center",
      },
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      duration: 1.25,
      ease: "none",
      yoyo: false,
    });
  }
}

export class HomeSectionTitleAnimation {
  private element: HTMLElement;
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateSectionTitle();
      });
    }
  }

  private animateSectionTitle() {
    document.fonts.ready.then(() => {
      gsap.set(this.element, { opacity: 1 });
      let split;

      SplitText.create(this.element, {
        type: "lines",
        tag: "span",
        mask: "lines",
        onSplit: (self) => {
          split = gsap.from(self.lines, {
            y: -20,
            autoAlpha: 0,
            stagger: 0.25,
            duration: 1,
            ease: "expo.out",
            scrollTrigger: {
              trigger: this.element,
              markers: false,
              start: "top bottom-=250px",
            },
            onComplete: () => {
              self.revert();
            },
          });
          return split;
        },
      });
    });
  }
}

export class HomeSectionIntroAnimation {
  private element: HTMLElement;
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateSectionIntro();
      });
    }
  }

  private animateSectionIntro() {
    gsap.from(this.element, {
      autoAlpha: 0,
      duration: 1,
      ease: "expo.out",
      scrollTrigger: {
        trigger: this.element,
        markers: false,
        start: "top bottom-=250px",
      },
    });
  }
}

export class HomeOrangeShapeAnimation {
  private element: HTMLElement;
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateOrangeShape();
      });
    }
  }

  private animateOrangeShape() {
    gsap.to(this.element, {
      "--home-orange-shape-scale-y": 1,
      duration: 1,
      ease: "expo.out",
      scrollTrigger: {
        trigger: this.element,
        markers: false,
        start: "top bottom-=250px",
      },
    });
  }
}

export class HomeBottomCTAAnimation {
  private element: HTMLElement;
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.init();
    }
  }

  private init() {
    if (!prefersReducedMotion) {
      mm.add("(min-width: 1024px)", () => {
        this.animateCTA();
      });
    }
  }

  private animateCTA() {
    gsap.from(this.element, {
      "--home-cta-background-stop1": "60%", // starts at 0%, animates to 22%
      "--home-cta-background-stop2": "100%", // starts at 40%, animates to 61%
      ease: "none",
      scrollTrigger: {
        trigger: this.element,
        markers: false,
        start: "top bottom-=250px",
        scrub: true,
      },
    });
  }
}

export default function homeAnimationsInit() {
  const homeHero = document.querySelector(".home-hero") as HTMLElement;
  new HomeHeroAnimation(homeHero);

  const homeFeaturette = document.querySelector(
    ".home-hero-alert-feature-wrap .content-feature-set--featurette",
  ) as HTMLElement;

  new HomeFeaturetteAnimation(homeFeaturette);

  const homeSectionTitles = document.querySelectorAll(
    ".home-hero-alert-feature-wrap .content-feature-set--triple .component-heading, body:has(.home-hero) .paragraph-widget:has(.wpra-display) .text-content > h2",
  ) as NodeListOf<HTMLElement>;

  for (let i = 0; i < homeSectionTitles.length; i++) {
    new HomeSectionTitleAnimation(homeSectionTitles[i]);
  }

  const homeCTAImage = document.querySelector(
    "body:has(.home-hero) .call-to-action--dramatic .call-to-action__media",
  ) as HTMLElement;

  new HomeImageAnimation(homeCTAImage);

  const homeSectionIntros = document.querySelectorAll(
    ".home-hero-alert-feature-wrap .content-feature-set--triple .component-intro, body:has(.home-hero) .paragraph-widget:has(.wpra-display) .text-content > h2 + p",
  ) as NodeListOf<HTMLElement>;

  for (let i = 0; i < homeSectionIntros.length; i++) {
    new HomeSectionIntroAnimation(homeSectionIntros[i]);
  }

  const homeSectionOrangeShapes = document.querySelectorAll(
    ".home-hero-alert-feature-wrap .content-feature-set--triple, body:has(.home-hero) .paragraph-widget:has(.wpra-display)",
  ) as NodeListOf<HTMLElement>;

  for (let i = 0; i < homeSectionOrangeShapes.length; i++) {
    new HomeOrangeShapeAnimation(homeSectionOrangeShapes[i]);
  }

  const homeBottomCTA = document.querySelector(
    "body:has(.home-hero) .paragraph-widget--call-to-action:last-of-type .call-to-action__inner",
  ) as HTMLElement;

  new HomeBottomCTAAnimation(homeBottomCTA);
}
